<template>
	<contentBox v-if="!loadError" v-bind:loading="!recLoaded">
		<contentTitle v-if="zoning" label="ZONING" close="home">
			<h2 class="text-white font-weight-light pb-2">{{zoning.designation}}</h2>
		</contentTitle>
		<contentBody v-if="zoning" v-bind:locked="token === null">
			<contentActions v-if="zoning">
				<button class="btn btn-outline-light btn-sm" v-bind:class="{active:shareShown === true}" v-on:click="share">
					<i class="material-icons md-18" title="share zoning">share</i> 
					Share
				</button>
				<button class="btn btn-outline-light btn-sm" v-on:click="zoomTo">
					<i class="material-icons md-18" title="center map on zoning">place</i> 
					Center
				</button>
				<button v-if="isAdmin" class="btn btn-outline-light btn-sm" v-on:click="edit">
					<i class="material-icons md-18" title="center map on overlay">edit</i> 
					Edit
				</button>
			</contentActions>
			<share v-if="shareShown" v-bind:link="location" @close="share"></share>
			<div class="p-3">
				<div class="lock-block" v-if="token === null">
					<div class="row mx-0 hover-dk" v-on:click="routeLogin">
						<div class="col-2 text-center my-auto">
							<i class="material-icons text-light mr-3 md-36">account_box</i>
						</div>
						<div class="py-3 col-10 pl-0 pr-3 py-3">
							<h5 class="mb-0 text-white">Sign in to view</h5>
							<h5 class="mb-0 font-weight-light text-light">Get details on density, size restrictions, and jurisdiction</h5>
						</div>
					</div>
				</div>
				<div class="category-body">
					<div class="category">
						<div class="category-title">DESCRIPTION</div>
						<div class="property-row">
							<div class="text-description property-locked">{{zoning.description || 'No description available'}}</div>
						</div>
					</div>

					<div class="category">
						<div class="category-title">USE</div>
						<div class="property-row">
							<label>Primary</label>
							<span v-if="zoning.use.primary" class="property-value-right">
								{{zoning.use.primary}}
							</span>
							<span v-else class="undef property-value-right">
								-
							</span>
						</div>
						<div class="property-row">
							<label>Secondary</label>
							<span v-if="zoning.use.secondary" class="property-value-right">
								{{zoning.use.secondary}}
							</span>
							<span v-else class="undef property-value-right">
								-
							</span>
						</div>
					</div>
					
					<div v-if="zoning.regulations && zoning.regulations.length > 0" v-for="(regulation, regInd) in zoning.regulations" class="pt-1 pb-0 clearfix position-relative">
						<div v-if="regulation.lotSize.minArea !== null || regulation.lotSize.maxArea !== null ||
							regulation.lotSize.minWidth !== null || regulation.lotSize.minWidthCorner !== null || 
							regulation.lotSize.minFrontage !== null || regulation.lotSize.minDepth !== null" class="category">
							<div class="category-title">LOT SIZE</div>
							<div class="property-row" v-if="regulation.lotSize.minArea !== null">
								<label>Minimum area</label>

								<div class="property-value-right property-locked">
									{{regulation.lotSize.minArea | integerLg}} ft²
								</div>
							</div>
							<div class="property-row" v-if="regulation.lotSize.maxArea !== null">
								<label>Maximum area</label>
								<div class="property-value-right property-locked">
									{{regulation.lotSize.maxArea | integerLg}} ft²
								</div>
							</div>
							<div class="property-row" v-if="regulation.lotSize.minWidth !== null">
								<label>Minimum width</label>
								<div class="property-value-right property-locked">
									{{regulation.lotSize.minWidth | integerLg}} ft
								</div>
							</div>
							<div class="property-row" v-if="regulation.lotSize.minWidthCorner !== null">
								<label>Minimum width corner</label>
								<div class="property-value-right property-locked">
									{{regulation.lotSize.minWidthCorner | integerLg}} ft
								</div>
							</div>
							<div class="property-row" v-if="regulation.lotSize.minFrontage !== null">
								<label>Minimum frontage</label>
								<div class="property-value-right property-locked">
									{{regulation.lotSize.minFrontage | integerLg}} ft
								</div>
							</div>
							<div class="property-row" v-if="regulation.lotSize.minDepth !== null">
								<label>Minimum depth</label>
								<div class="property-value-right property-locked">
									{{regulation.lotSize.minDepth | integerLg}} ft
								</div>
							</div>
						</div>
						<div v-if="regulation.density.sfDu !== null" class="category">
							<div class="category-title">DENSITY</div>
							<div class="property-row">
								<label>SF/DU</label>
								<div class="property-value-right property-locked">
									{{regulation.density.sfDu | integerLg}}
								</div>
							</div>
							<div class="property-row" v-if="regulation.density.sfDuNote !== null"> 
								<label>SF/DU note</label>
								<div class="property-value-right property-locked">
									{{regulation.density.sfDuNote}}
								</div>
							</div>
						</div>
						<div v-if="regulation.heightLimit.max !== null || regulation.heightLimit.aboveEnclosedParking !== null || regulation.heightLimit.roofFlat !== null || regulation.heightLimit.roofPitched !== null" class="category">
							<div class="category-title">HEIGHT LIMIT</div>
							<div class="property-row" v-if="regulation.heightLimit.max !== null">
								<label>Height maximum</label>
								<div class="property-value-right property-locked">
									{{regulation.heightLimit.max | integerLg}} ft
								</div>
							</div>
							<div class="property-row" v-if="regulation.heightLimit.aboveEnclosedParking !== null">
								<label>Above enclosed parking</label>
								<div class="property-value-right property-locked">
									{{regulation.heightLimit.aboveEnclosedParking | integerLg}} ft
								</div>
							</div>
							<div class="property-row" v-if="regulation.heightLimit.roofFlat !== null">
								<label>Roof flat limit</label>
								<div class="property-value-right property-locked">
									{{regulation.heightLimit.roofFlat | integerLg}} ft
								</div>
							</div>
							<div class="property-row" v-if="regulation.heightLimit.roofPitched !== null">
								<label>Roof pitched limit</label>
								<div class="property-value-right property-locked">
									{{regulation.heightLimit.roofPitched | integerLg}} ft
								</div>
							</div>
							<div class="property-row" v-if="regulation.heightLimit.note !== null">
								<label>Height note</label>
								<div class="property-value-right property-locked">
									{{regulation.heightLimit.note}} ft
								</div>
							</div>
						</div>
						<div v-if="regulation.far.base !== null || regulation.far.min !== null || 
							regulation.far.max !== null || regulation.far.residential !== null || 
							regulation.far.commercial !== null || regulation.far.mixed !== null || 
							regulation.far.note !== null" class="category">
							<div class="category-title">FLOOR TO AREA RATIO (FAR)</div>
							<div class="property-row" v-if="regulation.far.base !== null">
								<label>FAR base</label>
								<div class="property-value-right property-locked">
									{{regulation.far.base}}
								</div>
							</div>
							<div class="property-row" v-if="regulation.far.min !== null">
								<label>FAR minimum</label>
								<div class="property-value-right property-locked">
									{{regulation.far.min}}
								</div>
							</div>
							<div class="property-row" v-if="regulation.far.max !== null">
								<label>FAR maximum</label>
								<div class="property-value-right property-locked">
									{{regulation.far.max}}
								</div>
							</div>
							<div class="property-row" v-if="regulation.far.residential !== null">
								<label>FAR residential</label>
								<div class="property-value-right property-locked">
									{{regulation.far.residential}}
								</div>
							</div>
							<div class="property-row" v-if="regulation.far.commercial !== null">
								<label>FAR commercial</label>
								<div class="property-value-right property-locked">
									{{regulation.far.commercial}}
								</div>
							</div>
							<div class="property-row" v-if="regulation.far.mixed !== null">
								<label>FAR mixed</label>
								<div class="property-value-right property-locked">
									{{regulation.far.mixed}}
								</div>
							</div>
							<div class="property-row" v-if="regulation.far.note !== null">
								<label>FAR note</label>
								<div class="property-value-right property-locked">
									{{regulation.far.note}}
								</div>
							</div>
						</div>
						<div v-if="regulation.setbacks.frontMin !== null || regulation.setbacks.frontMax !== null || 
							regulation.setbacks.interiorSide !== null || regulation.setbacks.streetSide !== null || 
							regulation.setbacks.rear !== null || regulation.setbacks.rearAlley !== null ||
							regulation.setbacks.rearAlley !== null || regulation.setbacks.generalNote !== null" class="category">
							<div class="category-title">SETBACKS</div>
							<div class="property-row" v-if="regulation.setbacks.frontMin !== null">
								<label>Front minimum</label>
								<div class="property-value-right property-locked">
									{{regulation.setbacks.frontMin | integerLg}} ft
								</div>
							</div>
							<div class="property-row" v-if="regulation.setbacks.frontMinNote !== null">
								<label>Front minimum note</label>
								<div class="property-value-right property-locked">
									{{regulation.setbacks.frontMinNote}}
								</div>
							</div>
							<div class="property-row" v-if="regulation.setbacks.frontMax !== null">
								<label>Front maximum</label>
								<div class="property-value-right property-locked">
									{{regulation.setbacks.frontMax | integerLg}} ft
								</div>
							</div>
							<div class="property-row" v-if="regulation.setbacks.frontMaxNote !== null">
								<label>Front maximum note</label>
								<div class="property-value-right property-locked">
									{{regulation.setbacks.frontMaxNote}}
								</div>
							</div>
							<div class="property-row" v-if="regulation.setbacks.interiorSide !== null">
								<label>Interior side</label>
								<div class="property-value-right property-locked">
									{{regulation.setbacks.interiorSide | integerLg}} ft
								</div>
							</div>
							<div class="property-row" v-if="regulation.setbacks.interiorSideNote !== null">
								<label>Interior side note</label>
								<div class="property-value-right property-locked">
									{{regulation.setbacks.interiorSideNote}}
								</div>
							</div>
							<div class="property-row" v-if="regulation.setbacks.streetSide !== null">
								<label>Street side</label>
								<div class="property-value-right property-locked">
									{{regulation.setbacks.streetSide | integerLg}} ft
								</div>
							</div>
							<div class="property-row" v-if="regulation.setbacks.streetSideNote !== null">
								<label>Street side note</label>
								<div class="property-value-right property-locked">
									{{regulation.setbacks.streetSideNote}}
								</div>
							</div>
							<div class="property-row" v-if="regulation.setbacks.rear !== null">
								<label>Rear</label>
								<div class="property-value-right property-locked">
									{{regulation.setbacks.rear | integerLg}} ft
								</div>
							</div>
							<div class="property-row" v-if="regulation.setbacks.rearAlley !== null">
								<label>Rear Alley</label>
								<div class="property-value-right property-locked">
									{{regulation.setbacks.rearAlley | integerLg}} ft
								</div>
							</div>
							<div class="property-row" v-if="regulation.setbacks.rearNote !== null">
								<label>Rear note</label>
								<div class="property-value-right property-locked">
									{{regulation.setbacks.rearNote}}
								</div>
							</div>
							<div class="property-row" v-if="regulation.setbacks.generalNote !== null">
								<label>General note</label>
								<div class="property-value-right property-locked">
									{{regulation.setbacks.generalNote}}
								</div>
							</div>
						</div>
						<div v-if="regulation.lotCoverage.min !== null || regulation.lotCoverage.max !== null" class="category">
							<div class="category-title">LOT COVERAGE</div>
							<div class="property-row" v-if="regulation.lotCoverage.min !== null">
								<label>Lot coverage minimum</label>
								<div class="property-value-right property-locked">
									{{regulation.lotCoverage.min | integerLg}} ft
								</div>
							</div>
							<div class="property-row" v-if="regulation.lotCoverage.max !== null">
								<label>Lot coverage maximum</label>
								<div class="property-value-right property-locked">
									{{regulation.lotCoverage.max | integerLg}} ft
								</div>
							</div>
							<div class="property-row" v-if="regulation.lotCoverage.note !== null">
								<label>Lot coverage note</label>
								<div class="property-value-right property-locked">
									{{regulation.lotCoverage.note}}
								</div>
							</div>
						</div>
					</div>
					<div v-if="zoning.jurisdiction !== null && zoning.jurisdiction.name !== null" class="category">
						<div class="category-title">JURISDICTION</div>
						<div class="property-row">
							<div class="text-description property-locked">{{zoning.jurisdiction.name}}</div>
						</div>
					</div>
					<div v-if="zoning.references && zoning.references.length > 0" class="category">
						<div class="category-title">REFERENCES</div>
						<div v-for="(ref, idx) in zoning.references">
							<div class="property-row">
								<label>{{ref.description}}</label>
								<div class="float-right font-weight-bold">
									<a :href="ref.url" target="_blank"><i class="material-icons md-12">open_in_new</i> Link</a>
								</div>
							</div>
						</div>
					</div>
					<div class="over-action d-flex" v-if="token === null" v-on:click="routeLogin">
						<div class="over-action-box align-self-center text-white hover-dk">
							<div class="text-center">
								<i class="material-icons text-light md-36">account_box</i>
							</div>
							<div class="text-center">
								Sign in to view
							</div>
						</div>
					</div>
				</div>
			</div>
		</contentBody>
	</contentBox>
</template>

<script>

import contentBox from '../content';
import contentActions from '../content/content-actions';
import contentBody from '../content/content-body';
import contentTitle from '../content/content-title';
import share from '../share/share';

export default {
	name: 'zoning',

	components:{
		contentBox,
		contentActions,
		contentBody,
		contentTitle,
		share
	},

	computed: {
		flyToDone:function(){
			return this.$store.state.map.flyToDone;
		},
		isAdmin: function(){
			return this.$store.state.agent.rec && this.$store.state.agent.rec.role.name && this.$store.state.agent.rec.role.name === 'admin';
		},
		location:function(){
			return window.location.href;
		},
		mapCenter: function(){
			return {lat:this.$store.state.map.params.lat,lng:this.$store.state.map.params.lng};
		},
		mapZoom: function(){
			return this.$store.state.map.params.zoom;
		},
		mapLoaded:function(){
			return this.$store.state.map.loaded;
		},
		mapBounds:function(){
			return this.$store.state.map.bounds;
		},
		token: function(){
			return this.$store.state.agent.token;
		},
		zoning: function() {
			return this.$store.state.zoning.rec;
		},
	},

	created: function(){
		this.recLoaded = false;
		this.getRec();
	},
	data: function(){
		return {
			recLoaded:false,
			shareShown:false,
			loadError:null
		}
	},

	destroyed: function(){
		this.$store.dispatch('ZONING_HIGHLIGHT_CLEAR',{});
	},

	methods: {
		edit:function(){
			this.$router.push({
				name: 'zoning-edit',
				params:{
					id: this.$route.params.id
				}
			});
		},
		getRec: function(){
			this.loadError = null;
			this.recLoaded = false;
			this.$store.dispatch('ZONING_GET', {
				id: this.$route.params.id
			}).catch((error)=>{
				this.loadError = error;
			});
		},
		routeClose:function(){
			this.$router.push({
				name: 'home', 
				params:{}
			});
		},
		routeLogin:function(){
			this.$router.push({
				name: 'auth', 
				params:{}
			});
		},
		highlight:function(){
			this.$store.dispatch('ZONING_HIGHLIGHT',{});
		},
		zoomTo:function(){
			this.$store.dispatch('ZONING_ZOOM_TO'/*,this.mapCenter*/);
		},
		checkInside:function(){
			const bbox = this.zoning.bounds.bbox;
			const bounds = this.mapBounds;
			if (bbox[1] > bounds._ne.lat || bbox[3] < bounds._sw.lat || 
				bbox[0] > bounds._ne.lng || bbox[2] < bounds._sw.lng){
				return false;
			}
			return true;
		},
		checkZoom:function(){
			if (this.mapZoom >= 16 || this.mapZoom <= 12) return false;
			return true;
		},
		checkReady:function(){ //called to check if and resolve when all resources have been loaded
			if (!this.zoning || !this.mapLoaded) return;
			this.ready();
		},
		ready:function(){
			if (!this.checkInside()) this.zoomTo();
			else if (!this.checkZoom()){
				this.$store.dispatch('MAP_FLY_TO',{
					zoom:16
				});
			}
			else this.highlight();
			this.updateTitleTag();
		},
		updateTitleTag:function(){
			if (this.zoning && this.zoning.designation) return document.title = 'Zoning '+this.zoning.designation+' - Scoutred Maps';
			return document.title = 'Zoning - Scoutred Maps';
		},
		share:function(){
			if (this.shareShown){
				this.shareShown = false;
				return;
			}
			if (navigator.share){
				navigator.share({
				  title: document.title,
				  text: 'Zoning data for '+this.zoning.designation,
				  url: window.location.href,
				});
				return;
			}

			this.shareShown = true;
		}
	},

	watch: {
		$route:function(toRoute, fromRoute){
			if (fromRoute && toRoute.params.id !== fromRoute.params.id){ // route resource has changed
				this.getRec();
			}
		},
		mapLoaded:function(){
			this.checkReady();
		},
		zoning:function(toRec, fromRec){
			this.checkReady();
			if (this.zoning) this.recLoaded = true;
		},
		flyToDone:function(){
			this.highlight();
		}
	},
}

</script>



// WEBPACK FOOTER //
// src/components/zoning/zoning.vue